import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ArrowLeft, MessageCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ChatWindow from '../../components/chat/ChatWindow';

const ConversationsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await api.get('/api/chat/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
      confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800' },
      preparando: { label: 'Preparando', color: 'bg-orange-100 text-orange-800' },
      coleta: { label: 'Coleta', color: 'bg-purple-100 text-purple-800' },
      em_rota: { label: 'Em Rota', color: 'bg-indigo-100 text-indigo-800' },
      entregue: { label: 'Entregue', color: 'bg-green-100 text-green-800' },
      cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
      paid: { label: 'Pago', color: 'bg-green-100 text-green-800' },
      accepted: { label: 'Aceito', color: 'bg-blue-100 text-blue-800' },
      picked_up: { label: 'Coletado', color: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Entregue', color: 'bg-green-100 text-green-800' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getBackPath = () => {
    switch (user?.user_type) {
      case 'cliente': return '/cliente';
      case 'lojista': return '/lojista';
      case 'entregador': return '/entregador';
      case 'admin': return '/admin';
      default: return '/';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Mensagens</h1>
              <p className="text-sm text-gray-500">{conversations.length} conversa(s)</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto p-4">
        {conversations.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Nenhuma conversa</h2>
            <p className="text-gray-500">As mensagens dos seus pedidos aparecerão aqui.</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg divide-y">
            {conversations.map((conv) => (
              <div
                key={conv.order_id}
                onClick={() => setSelectedOrderId(conv.order_id)}
                className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-800">
                        Pedido #{conv.order_id.substring(0, 8)}
                      </span>
                      {getStatusBadge(conv.order_status)}
                      {conv.unread_count > 0 && (
                        <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {conv.last_message?.content || 'Sem mensagens'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className="text-xs text-gray-400">
                      {formatDate(conv.updated_at)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Chat Modal */}
      {selectedOrderId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-md">
            <ChatWindow 
              orderId={selectedOrderId} 
              onClose={() => {
                setSelectedOrderId(null);
                loadConversations(); // Refresh unread counts
              }} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationsList;
